import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeftIcon, CheckCircleIcon, MapPinIcon, ClockIcon, BriefcaseIcon, MailIcon, SendIcon, UploadIcon, UserIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { buildBreadcrumbList, buildJobPostingSchema } from '../lib/structuredData';
// Vacancy data store
const vacanciesData: Record<string, any> = {
  'stage-design': {
    title: 'Stage Design',
    type: 'Stage',
    location: 'Amsterdam / Hybride',
    hours: '32 - 40 uur',
    tags: ['UI/UX', 'Figma', 'Webdesign', 'Branding'],
    intro: 'Bij Noord Digital ontwerp je als design-stagiair mee aan websites en merken voor B2B-bedrijven. Je werkt nauw samen met Alex en het team aan échte projecten.',
    responsibilities: ['Wireframes en prototypes ontwerpen in Figma', 'Visuele uitwerkingen maken voor klantwebsites', 'Meedenken over UX en conversie', 'Visuele assets en iconen ontwerpen', 'Bijdragen aan de doorontwikkeling van het Noord Digital merk'],
    requirements: ['Je volgt een MBO/HBO opleiding (CMD, Mediavormgeving, Graphic Design)', 'Ervaring met Figma', 'Gevoel voor typografie, kleur en compositie', 'Proactief en leergierig', 'Goede beheersing van het Nederlands'],
    offer: ['Passende stagevergoeding', 'Begeleiding door ervaren designers', 'Erkende leerplek (S-BB)', 'Werken aan projecten die echt live gaan', 'Uitzicht op een vaste (bij)baan'],
    status: 'bezet'
  },
  'stage-account-manager': {
    title: 'Stage Account Manager',
    type: 'Stage',
    location: 'Amsterdam / Hybride',
    hours: '32 - 40 uur',
    tags: ['Sales', 'Klantcontact', 'Communicatie', 'Project coördinatie'],
    intro: 'Als stage account manager ben je de schakel tussen onze klanten en het Noord Digital team. Je leert hoe je sales, klantcontact en interne afstemming combineert tot tevreden klanten en gezonde groei.',
    responsibilities: ['Eerste aanspreekpunt voor klanten via mail, telefoon en gesprekken', 'Sales gesprekken voorbereiden en bijwonen', 'Behoeftes van klanten vertalen naar duidelijke briefings voor het team', 'Voortgang van projecten bewaken en intern coördineren', 'Meedenken over het verbeteren van het sales- en accountproces'],
    requirements: ['Je volgt een MBO/HBO opleiding (Commerciële Economie, Communicatie, Bedrijfskunde)', 'Sterk in communicatie, mondeling én schriftelijk', 'Energiek, ondernemend en niet bang om de telefoon te pakken', 'Goede beheersing van het Nederlands', 'Gestructureerde werkwijze'],
    offer: ['Passende stagevergoeding', 'Direct contact met echte klanten en deals', 'Begeleiding door Rocco (oprichter)', 'Erkende leerplek (S-BB)', 'Uitzicht op een vaste (bij)baan'],
    status: 'open'
  },
  'stage-developer': {
    title: 'Stage Developer',
    type: 'Stage',
    location: 'Amsterdam / Hybride',
    hours: '32 - 40 uur',
    tags: ['React', 'TypeScript', 'WordPress', 'Tailwind CSS'],
    intro: 'Bouw mee aan snelle, schaalbare websites en webapps voor B2B-klanten. Deze stage opent over een aantal maanden. Meld je nu vast aan en wij tippen je zodra de plek open is.',
    responsibilities: ['Meebouwen aan klantwebsites in WordPress en React', 'Pixel-perfect Figma designs omzetten naar code', 'Performance en Core Web Vitals optimaliseren', 'Bestaande projecten onderhouden en updaten', 'Meedenken over technische keuzes'],
    requirements: ['Je volgt een MBO/HBO opleiding (Software Development, Informatica)', 'Basis kennis van HTML, CSS, JavaScript', 'Ervaring met React of WordPress is een plus', 'Leergierig en oog voor detail', 'Goede beheersing van het Nederlands'],
    offer: ['Passende stagevergoeding', 'Begeleiding door ervaren developers', 'Erkende leerplek (S-BB)', 'Werken aan projecten die live gaan', 'Uitzicht op een vaste (bij)baan'],
    status: 'binnenkort',
    opensAt: '2026-09-05T00:00:00'
  }
};
export const VacaturePage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const vacancy = slug ? vacanciesData[slug] : null;
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    motivatie: ''
  });
  const [fileName, setFileName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  // Redirect to overview if vacancy not found
  useEffect(() => {
    if (!vacancy && slug) {
      navigate('/sollicitatie', {
        replace: true
      });
    }
  }, [vacancy, slug, navigate]);
  // Countdown logic
  useEffect(() => {
    if (vacancy?.status === 'binnenkort' && vacancy.opensAt) {
      const targetDate = new Date(vacancy.opensAt).getTime();
      const updateCountdown = () => {
        const now = new Date().getTime();
        const distance = targetDate - now;
        if (distance > 0) {
          setTimeLeft({
            days: Math.floor(distance / (1000 * 60 * 60 * 24)),
            hours: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
            minutes: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
            seconds: Math.floor(distance % (1000 * 60) / 1000)
          });
        }
      };
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [vacancy]);
  if (!vacancy) {
    return null; // Will redirect
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFileName(e.target.files[0].name);
    }
  };
  const renderCountdown = () => <div className="grid grid-cols-4 gap-2 text-center mt-4 mb-6" data-id="element-2890" data-mp="bf9zb816vb7p">
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2891" data-mp="3d9cx332pjmt">
        <div className="text-xl font-bold text-amber-700" data-id="element-2892" data-mp="3vhlb7eg2z68j">{timeLeft.days}</div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2893" data-mp="3vjobcray6fqz">
          Dagen
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2894" data-mp="8d5e6ukdywyo">
        <div className="text-xl font-bold text-amber-700" data-id="element-2895" data-mp="8f8h2f6n0kkt">{timeLeft.hours}</div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2896" data-mp="mau2kcdnwm8h">
          Uren
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2897" data-mp="w2giun4uxbi3">
        <div className="text-xl font-bold text-amber-700" data-id="element-2898" data-mp="w4kll1r0s4gs">
          {timeLeft.minutes}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2899" data-mp="r5gceb43i6qg">
          Min
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2900" data-mp="111t3r2c648i1">
        <div className="text-xl font-bold text-amber-700" data-id="element-2901" data-mp="10zq7w1oc3l5e">
          {timeLeft.seconds}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2902" data-mp="1kli7ranmfgdf">
          Sec
        </div>
      </div>
    </div>;
  return <div className="w-full min-h-screen bg-[#F7F8FA] text-[#111827] font-sans" data-id="element-2903" data-mp="1ao3wdpgk1hrz">
      <SEO title={`${vacancy.title} | Werken bij Noord Digital`} description={vacancy.intro} keywords={`${vacancy.title.toLowerCase()}, vacature, werken bij noord digital, amsterdam`} canonical={`https://noorddigital.nl/vacature/${slug}`} jsonLd={[buildJobPostingSchema({
      title: vacancy.title,
      description: vacancy.intro,
      path: `/vacature/${slug}`,
      employmentType: vacancy.type === 'Stage' ? 'INTERN' : 'FULL_TIME',
      datePosted: vacancy.opensAt ? vacancy.opensAt.slice(0, 10) : undefined
    }), buildBreadcrumbList([{
      name: 'Vacatures',
      path: '/sollicitatie'
    }, {
      name: vacancy.title,
      path: `/vacature/${slug}`
    }])]} data-id="element-2904" data-mp="1fnqhel2mk2bc" />
      <Header data-id="element-2905" data-mp="12hm11an4rcyy" />

      {/* Hero Section */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] pt-24 pb-32 relative overflow-hidden" data-id="element-2906" data-mp="12gawh8lv216s">
        <div className="absolute inset-0 opacity-10" data-id="element-2907" data-mp="ygsae9iazk2d">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2908" data-mp="tgeajequvgff"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1000px] relative z-10" data-id="element-2909" data-mp="jj9kv4xsx2tv">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-2910" data-mp="erhtis6tmt2o">
            <a href="/sollicitatie" className="inline-flex items-center text-white/80 hover:text-white transition-colors text-sm font-medium" data-id="element-2911" data-mp="aobt416e3w44">
              <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2912" data-mp="si6foyyim8m" />
              Terug naar alle vacatures
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-2913" data-mp="3rz33pqfluqe3">
            <div className="flex flex-wrap gap-3 mb-6" data-id="element-2914" data-mp="3msefrr2rsveh">
              <span className="inline-flex items-center bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm" data-id="element-2915" data-mp="3rt26ebn37a86">
                <BriefcaseIcon className="mr-2 h-4 w-4" data-id="element-2916" data-mp="l6izd5x1wu2" />
                {vacancy.type}
              </span>
              <span className="inline-flex items-center bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm" data-id="element-2917" data-mp="2z5oi11xgfmzo">
                <MapPinIcon className="mr-2 h-4 w-4" data-id="element-2918" data-mp="392ay7deu5pvd" />
                {vacancy.location}
              </span>
              <span className="inline-flex items-center bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm" data-id="element-2919" data-mp="3dtjaqibgprcr">
                <ClockIcon className="mr-2 h-4 w-4" data-id="element-2920" data-mp="2gd436oelbiev" />
                {vacancy.hours}
              </span>
              {vacancy.status === 'bezet' && <span className="inline-flex items-center bg-gray-500/80 text-white px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm border border-gray-400" data-id="element-2921" data-mp="1mj9hubs14hcn">
                  Op dit moment bezet
                </span>}
              {vacancy.status === 'binnenkort' && <span className="inline-flex items-center bg-amber-500/80 text-white px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm border border-amber-400" data-id="element-2922" data-mp="1whynn66nibcj">
                  Opent 5 september 2026
                </span>}
            </div>

            <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-2923" data-mp="18rmi21yvoqp4">
              {vacancy.title}
            </h1>

            <div className="flex flex-wrap gap-2 mb-8" data-id="element-2924" data-mp="1crwrxppsk199">
              {vacancy.tags.map((tag: string, index: number) => <span key={index} className="bg-[#005FCC] text-white text-sm px-3 py-1 rounded-md font-medium" data-id="element-2925" data-mp="1hlhg6q1l1gv9" data-mpi={index}>
                  {tag}
                </span>)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1000px] -mt-16 relative z-20 pb-24" data-id="element-2926" data-mp="hkw0ec92cvb0">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2927" data-mp="cl1dskwbmrvr">
          {/* Main Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="lg:col-span-2 space-y-8" data-id="element-2928" data-mp="8iwmvtx9c9ct">
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100" data-id="element-2929" data-mp="3uro0bzjojkzj">
              <h2 className="text-2xl font-bold text-[#111827] mb-4" data-id="element-2930" data-mp="3pze6w9jcw2si">
                Over de functie
              </h2>
              <p className="text-[#6B7280] text-lg leading-relaxed mb-8" data-id="element-2931" data-mp="3l1cq5v9ekfk7">
                {vacancy.intro}
              </p>

              <h3 className="text-xl font-bold text-[#111827] mb-4 mt-8" data-id="element-2932" data-mp="3c0eqtq9wsw7y">
                Wat ga je doen?
              </h3>
              <ul className="space-y-3 mb-8" data-id="element-2933" data-mp="3711u8dt8kas6">
                {vacancy.responsibilities.map((item: string, index: number) => <li key={index} className="flex items-start" data-id="element-2934" data-mp="2xco64r4xgg64" data-mpi={index}>
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mr-3 mt-0.5 flex-shrink-0" data-id="element-2935" data-mp="32csm8g1xb8vt" />
                    <span className="text-[#6B7280] leading-relaxed" data-id="element-2936" data-mp="2nfjlivqqcsrf">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <h3 className="text-xl font-bold text-[#111827] mb-4 mt-8" data-id="element-2937" data-mp="2sev1w3n6pw8h">
                Wat wij zoeken
              </h3>
              <ul className="space-y-3 mb-8" data-id="element-2938" data-mp="3l3aekjiug2gn">
                {vacancy.requirements.map((item: string, index: number) => <li key={index} className="flex items-start" data-id="element-2939" data-mp="36ee126y0px8n" data-mpi={index}>
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mr-3 mt-0.5 flex-shrink-0" data-id="element-2940" data-mp="36e47689ur5xo" />
                    <span className="text-[#6B7280] leading-relaxed" data-id="element-2941" data-mp="3bbnxw7nqlb6i">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <h3 className="text-xl font-bold text-[#111827] mb-4 mt-8" data-id="element-2942" data-mp="7pfelin6cpnl">
                Wat wij bieden
              </h3>
              <ul className="space-y-3" data-id="element-2943" data-mp="1sd804qc433rq">
                {vacancy.offer.map((item: string, index: number) => <li key={index} className="flex items-start" data-id="element-2944" data-mp="1nf6jsd62lijy" data-mpi={index}>
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mr-3 mt-0.5 flex-shrink-0" data-id="element-2945" data-mp="224voujhaa6pi" />
                    <span className="text-[#6B7280] leading-relaxed" data-id="element-2946" data-mp="1x59b88xjmog6">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="lg:col-span-1" data-id="element-2947" data-mp="14iev1o7reb8i">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 sticky top-28" data-id="element-2948" data-mp="1eh4dbi3khxpa">
              {vacancy.status === 'open' && <>
                  <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-2949" data-mp="2puyc8ma96ohs">
                    Enthousiast geworden?
                  </h3>
                  <p className="text-[#6B7280] mb-6 text-sm" data-id="element-2950" data-mp="3l669b6w3zr77">
                    Vul het sollicitatieformulier in en we nemen snel contact
                    met je op.
                  </p>

                  <a href="#solliciteer-formulier" className="flex items-center justify-center w-full bg-[#007AFF] hover:bg-[#005FCC] text-white font-semibold py-4 px-6 rounded-xl transition duration-300 shadow-md group mb-4" data-id="element-2951" data-mp="3u3gpn6nsa6iz">
                    <MailIcon className="mr-2 h-5 w-5" data-id="element-2952" data-mp="7w0hj5aggaph" />
                    Solliciteer direct
                  </a>

                  <p className="text-xs text-center text-gray-500" data-id="element-2953" data-mp="gqezjfmddzca">
                    We proberen binnen 2 werkdagen te reageren.
                  </p>
                </>}

              {vacancy.status === 'bezet' && <>
                  <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-2954" data-mp="qpdxa35lkaki">
                    Helaas, deze plek is bezet
                  </h3>
                  <p className="text-[#6B7280] mb-6 text-sm" data-id="element-2955" data-mp="vlbfkf0f7qzx">
                    We hebben momenteel al een topper gevonden voor deze stage.
                    Houd onze website in de gaten voor toekomstige plekken.
                  </p>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-center mb-4" data-id="element-2956" data-mp="266qynk9zv8l8">
                    <span className="text-gray-500 font-medium" data-id="element-2957" data-mp="218z74rd9hohk">
                      Solliciteren is nu niet mogelijk
                    </span>
                  </div>
                </>}

              {vacancy.status === 'binnenkort' && <>
                  <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-2958" data-mp="2txmvivtb4tzc">
                    Deze stage opent binnenkort
                  </h3>
                  <p className="text-[#6B7280] mb-2 text-sm" data-id="element-2959" data-mp="2k7l8m7coxhm0">
                    Zet het in je agenda! Vanaf 5 september 2026 kun je
                    solliciteren op deze stageplek.
                  </p>

                  {renderCountdown()}

                  <a href="mailto:info@noorddigital.nl?subject=Interesse%20in%20Stage%20Developer" className="flex items-center justify-center w-full bg-white border-2 border-[#007AFF] text-[#007AFF] hover:bg-blue-50 font-semibold py-3 px-6 rounded-xl transition duration-300 mb-4" data-id="element-2960" data-mp="3cued90t7b11a">
                    <MailIcon className="mr-2 h-5 w-5" data-id="element-2961" data-mp="117knbkd64riv" />
                    Laat je gegevens achter
                  </a>
                </>}

              <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-2962" data-mp="16540si21l09r">
                <div className="flex items-center gap-4" data-id="element-2963" data-mp="s64dbdq9mrf6">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-2964" data-mp="w9slnymq1r1h">
                    <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-full h-full object-cover" data-id="element-2965" data-mp="ib2mmdgcdhah" />
                  </div>
                  <div data-id="element-2966" data-mp="2cou1brdj2rcs">
                    <p className="text-sm font-bold text-[#111827]" data-id="element-2967" data-mp="2clg04mgloo6x">
                      Vragen over deze rol?
                    </p>
                    <a href="mailto:info@noorddigital.nl" className="text-sm text-[#007AFF] hover:underline" data-id="element-2968" data-mp="5yqrgy9avdfs">
                      Stuur ons een berichtje
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Conditional Form Section */}
      <div id="solliciteer-formulier" className="w-full bg-white py-20 md:py-24" data-id="element-2969" data-mp="ax22hcbbztzl">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[700px]" data-id="element-2970" data-mp="oogkbfti5p52">
          {vacancy.status === 'open' ? <>
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-2971" data-mp="jsrj781u406n">
                <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2972" data-mp="30esmysep689c">
                  Solliciteer op {vacancy.title}
                </h2>
                <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2973" data-mp="2vi2ccklnw4vb">
                  Vul onderstaand formulier in en voeg je CV toe. We nemen
                  binnen 2 werkdagen contact met je op.
                </p>
              </motion.div>

              {isSubmitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="bg-[#F7F8FA] rounded-2xl p-12 text-center border border-gray-100" data-id="element-2974" data-mp="2jdwlvf2rci37">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2975" data-mp="1qqiwq35awqtn">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" data-id="element-2976" data-mp="20mlyv905kpgl" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-2977" data-mp="24npdjs6gsfg8">
                    Bedankt voor je sollicitatie!
                  </h3>
                  <p className="text-[#6B7280] text-lg mb-8" data-id="element-2978" data-mp="3c0r7ifbb9ni9">
                    We hebben je gegevens ontvangen en nemen binnen 2 werkdagen
                    contact met je op.
                  </p>
                  <a href="/sollicitatie" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors" data-id="element-2979" data-mp="14f9774e5jz81">
                    <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2980" data-mp="vn77x9ftkozc" />
                    Bekijk alle vacatures
                  </a>
                </motion.div> : <motion.form initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} onSubmit={handleSubmit} className="bg-[#F7F8FA] rounded-2xl p-8 md:p-10 border border-gray-100" data-id="element-2981" data-mp="qnkuozzzr8qj">
                  <div className="space-y-6" data-id="element-2982" data-mp="7prqludmssrk">
                    {/* Naam */}
                    <div data-id="element-2983" data-mp="7ruthezvugdp">
                      <label htmlFor="naam" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2984" data-mp="2735odydbudal">
                        Naam *
                      </label>
                      <div className="relative" data-id="element-2985" data-mp="221gdqju0o1n1">
                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2986" data-mp="2vd2ihubwb1k9" />
                        <input type="text" id="naam" required value={formData.naam} onChange={e => setFormData({
                    ...formData,
                    naam: e.target.value
                  })} placeholder="Je volledige naam" className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" data-id="element-2987" data-mp="2qgc4dcjnig1h" />
                      </div>
                    </div>

                    {/* Email */}
                    <div data-id="element-2988" data-mp="22r9nw80r6t8k">
                      <label htmlFor="email" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2989" data-mp="1xqveomnf1d8y">
                        E-mailadres *
                      </label>
                      <div className="relative" data-id="element-2990" data-mp="vpify58auyj">
                        <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2991" data-mp="3s5eicsjp1msk" />
                        <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} placeholder="je@email.nl" className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" data-id="element-2992" data-mp="3hq65gtdo7hl" />
                      </div>
                    </div>

                    {/* Telefoon */}
                    <div data-id="element-2993" data-mp="3lpxrixa335p1">
                      <label htmlFor="telefoon" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2994" data-mp="3qmo8rnxmk7c1">
                        Telefoonnummer
                      </label>
                      <div className="relative" data-id="element-2995" data-mp="15wyupd4qwpke">
                        <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2996" data-mp="15w6z42b5eru4" />
                        <input type="tel" id="telefoon" value={formData.telefoon} onChange={e => setFormData({
                    ...formData,
                    telefoon: e.target.value
                  })} placeholder="06 - 12345678" className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" data-id="element-2997" data-mp="1au8h0jwu8l40" />
                      </div>
                    </div>

                    {/* Vacature (read-only) */}
                    <div data-id="element-2998" data-mp="2ja5egdryzerb">
                      <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2999" data-mp="2jawyqsg49q2a">
                        Vacature
                      </label>
                      <div className="relative" data-id="element-3000" data-mp="2sabqdv80s6od">
                        <BriefcaseIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3001" data-mp="2s9ig4k9uqz1h" />
                        <input type="text" readOnly value={vacancy.title} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-[#6B7280] cursor-not-allowed" data-id="element-3002" data-mp="24iwizo1dyz69" />
                      </div>
                    </div>

                    {/* CV Upload */}
                    <div data-id="element-3003" data-mp="1v6pb4g2prosx">
                      <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-3004" data-mp="1v80g2j7wb2lm">
                        CV uploaden *
                      </label>
                      <label htmlFor="cv-upload" className="flex items-center justify-center w-full py-8 bg-white border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#007AFF] hover:bg-blue-50/30 transition-all group" data-id="element-3005" data-mp="1lh5qb4vfxkvf">
                        <div className="text-center" data-id="element-3006" data-mp="2e2ffg9y4cx9z">
                          <UploadIcon className="h-8 w-8 text-gray-400 group-hover:text-[#007AFF] mx-auto mb-2 transition-colors" data-id="element-3007" data-mp="1z6hduc5ff0qd" />
                          {fileName ? <p className="text-sm font-medium text-[#007AFF]" data-id="element-3008" data-mp="248f1dikv0hp4">
                              {fileName}
                            </p> : <>
                              <p className="text-sm font-medium text-[#6B7280]" data-id="element-3009" data-mp="ss8ad8epc05u">
                                Klik om je CV te uploaden
                              </p>
                              <p className="text-xs text-gray-400 mt-1" data-id="element-3010" data-mp="147bj0ybsu1uu">
                                PDF, DOC of DOCX (max. 5MB)
                              </p>
                            </>}
                        </div>
                        <input type="file" id="cv-upload" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" data-id="element-3011" data-mp="ub04s2snf1xr" />
                      </label>
                    </div>

                    {/* Motivatie */}
                    <div data-id="element-3012" data-mp="1mz4gb4xwi98d">
                      <label htmlFor="motivatie" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-3013" data-mp="1hz9liqb08zi1">
                        Korte motivatie
                      </label>
                      <textarea id="motivatie" rows={5} value={formData.motivatie} onChange={e => setFormData({
                  ...formData,
                  motivatie: e.target.value
                })} placeholder="Vertel kort waarom je bij Noord Digital wilt werken en wat je motiveert voor deze rol..." className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all resize-none" data-id="element-3014" data-mp="1d29fgfe5nwqx" />
                    </div>
                  </div>

                  {/* Submit */}
                  <button type="submit" className="mt-8 w-full flex items-center justify-center bg-[#007AFF] hover:bg-[#005FCC] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl group" data-id="element-3015" data-mp="2rz2oej028u2l">
                    <SendIcon className="mr-2 h-5 w-5 group-hover:translate-x-0.5 transition-transform" data-id="element-3016" data-mp="35wr6jj2qtn3y" />
                    Verstuur sollicitatie
                  </button>

                  <p className="text-xs text-center text-gray-500 mt-4" data-id="element-3017" data-mp="1bizr22xqg90l">
                    Door te solliciteren ga je akkoord met ons{' '}
                    <a href="/privacy" className="text-[#007AFF] hover:underline" data-id="element-3018" data-mp="1givt3wmvdqdi">
                      privacybeleid
                    </a>
                    .
                  </p>
                </motion.form>}
            </> : vacancy.status === 'bezet' ? <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-[#F7F8FA] rounded-2xl p-12 text-center border border-gray-100" data-id="element-3019" data-mp="14i5u41bjila1">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3020" data-mp="1repl6l8nj2ie">
                <BriefcaseIcon className="h-8 w-8 text-gray-500" data-id="element-3021" data-mp="1nckvtyp8ks1k" />
              </div>
              <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-3022" data-mp="215akkaw5gb2d">
                Deze stage is op dit moment bezet
              </h3>
              <p className="text-[#6B7280] text-lg mb-8 max-w-lg mx-auto" data-id="element-3023" data-mp="2b404e97lw5l9">
                We hebben momenteel al een geschikte kandidaat gevonden voor de
                rol van {vacancy.title}. Houd onze website in de gaten voor
                toekomstige stagemogelijkheden.
              </p>
              <a href="/sollicitatie" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors" data-id="element-3024" data-mp="2tsk41c81yq3u">
                <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-3025" data-mp="2frziw9eo0i76" />
                Terug naar alle vacatures
              </a>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-[#F7F8FA] rounded-2xl p-12 text-center border border-gray-100" data-id="element-3026" data-mp="2ktylhh2kzb32">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3027" data-mp="1x3kuzuzn9kal">
                <ClockIcon className="h-8 w-8 text-amber-600" data-id="element-3028" data-mp="221m8m0eawreo" />
              </div>
              <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-3029" data-mp="1n5eu9gpkqk7b">
                Stage opent op 5 september 2026
              </h3>
              <p className="text-[#6B7280] text-lg mb-8 max-w-lg mx-auto" data-id="element-3030" data-mp="1s513lf2a7gau">
                Deze stageplek is momenteel nog niet open voor sollicitaties. Je
                kunt ons wel alvast een berichtje sturen als je interesse hebt!
              </p>
              <div className="max-w-md mx-auto mb-8" data-id="element-3031" data-mp="1yj8c92i5efzp">{renderCountdown()}</div>
              <a href="mailto:info@noorddigital.nl?subject=Interesse%20in%20Stage%20Developer" className="inline-flex items-center justify-center bg-[#007AFF] hover:bg-[#005FCC] text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 shadow-md" data-id="element-3032" data-mp="1tm85epdh597j">
                <MailIcon className="mr-2 h-5 w-5" data-id="element-3033" data-mp="1jtso7ua04onp" />
                Laat je gegevens achter
              </a>
            </motion.div>}
        </div>
      </div>

      <Footer data-id="element-3034" data-mp="1aw8k8dkr8e9e" />
    </div>;
};